<template>
  <InfusePageLayout>
    <div class="flex flex-col gap-6 overflow-y-auto">
      <ChargingStationTable
        :create-enabled="true"
        :import-enabled="true"
        :editable="true"
        :loading="isLoading"
        :removable="true"
        :rows="chargingStations"
        :search-enabled="true"
        :search-query="searchQuery"
        :has-more-items="hasNextPage"
        @create="createModalVisible = true"
        @edit="editRow"
        @click-row="(chargingStation: ChargingStation) => redirectToDetails(router, chargingStation.id, 'charging-stations.overview')"
        @remove="(chargingStation: ChargingStation) => confirmRemove(chargingStation)"
        @search="(query: string) => search(query)"
        @import="importModalVisible = true"
        @load="!isFetching && fetchNextPage()"
      />

      <CreateChargingStationModal
        v-if="createModalVisible"
        :loading="isCreating"
        :network-profiles="networkProfiles"
        :basic-auth-deviations="basicAuthDeviations"
        @create="(chargingStation: CreateChargingStation) => create(chargingStation)"
        @close="createModalVisible = false"
      />

      <UpdateChargingStationModal
        v-if="updateModalVisible && selectedRow"
        :charging-station="selectedRow"
        :loading="isUpdating"
        :network-profiles="networkProfiles"
        :basic-auth-deviations="basicAuthDeviations"
        @update="(chargingStation: ChargingStation) => update(chargingStation)"
        @close="updateModalVisible = false"
      />

      <InfuseConfirmModal
        v-if="removeModalVisible"
        :action-title="t('e_connector.chargingStations.modal.remove.title')"
        verification-text="delete"
        @confirm="remove"
        @close="removeModalVisible = false"
      />

      <ImportChargingStationsModal
        v-if="importModalVisible"
        :loading="isImporting"
        @close="importModalVisible = false"
        @import="(file: File) => importFile(file)"
      />

      <ImportChargingStationsResponseModal
        v-if="importResultModalVisible"
        :import-response="importResponse"
        @close="importResultModalVisible = false"
        @retry="() => retryImport()"
      />
    </div>
  </InfusePageLayout>
</template>

<script setup lang="ts">
import { InfusePageLayout } from '@infuse/ui/layouts';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import {
  ChargingStationTable,
  CreateChargingStationModal,
  ImportChargingStationsModal,
  ImportChargingStationsResponseModal,
  UpdateChargingStationModal
} from '@infuse/e-connector/charging-station/ui';
import { replaceQueryParams } from '@infuse/ui/utils';
import { ChargingStation, ImportChargeStationsResponse } from '@infuse/e-connector/charging-station/domain';
import { redirectToDetails } from '@infuse/e-connector/shared/utils';
import { useNetworkProfiles } from '@infuse/e-connector/network-profile/queries';
import { useBasicAuthDeviations } from '@infuse/e-connector/basic-auth-deviation/queries';
import { useChargingStations } from '@infuse/e-connector/charging-station/queries';
import {
  useCreateChargingStation,
  useDeleteChargingStation,
  useImportChargingStations,
  useUpdateChargingStation
} from '@infuse/e-connector/charging-station/mutations';
import { CreateChargingStation } from '@infuse/api/e-connector/charging-station';
import { InfuseConfirmModal } from '@infuse/ui/components';
import { ECONNECTOR_MAX_PAGE_SIZE } from '@infuse/e-connector/shared/queries';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const { chargingStations, searchQuery, isLoading, hasNextPage, isFetching, fetchNextPage } = useChargingStations();
const { networkProfiles, fetchAllPages: fetchAllNetworkProfiles } = useNetworkProfiles(ECONNECTOR_MAX_PAGE_SIZE);
const { basicAuthDeviations } = useBasicAuthDeviations();
const { mutateAsync: createChargingStation, isPending: isCreating } = useCreateChargingStation();
const { mutateAsync: updateChargingStation, isPending: isUpdating } = useUpdateChargingStation();
const { mutateAsync: deleteChargingStation } = useDeleteChargingStation();
const { mutateAsync: importChargingStations, isPending: isImporting } = useImportChargingStations();

const createModalVisible = ref<boolean>(false);
const updateModalVisible = ref<boolean>(false);
const removeModalVisible = ref<boolean>(false);
const importModalVisible = ref<boolean>(false);
const selectedRow = ref<ChargingStation>();
const importResultModalVisible = ref<boolean>(false);
const importResponse = ref<ImportChargeStationsResponse>();

onMounted(async () => {
  await fetchAllNetworkProfiles();
});

watch(
  () => route.query.search,
  (search) => {
    searchQuery.value = search?.toString();
  },
  { immediate: true }
);

function search(search: string) {
  searchQuery.value = search;
  replaceQueryParams(router, 'charging-stations', { search: searchQuery.value });
}

async function create(chargingStation: CreateChargingStation) {
  await createChargingStation(chargingStation);
  createModalVisible.value = false;
}

async function update(chargingStation: ChargingStation) {
  await updateChargingStation(chargingStation);
  updateModalVisible.value = false;
}

function editRow(row: ChargingStation) {
  selectedRow.value = row;
  updateModalVisible.value = true;
}

function confirmRemove(row: ChargingStation) {
  selectedRow.value = row;
  removeModalVisible.value = true;
}

async function remove() {
  if (!selectedRow.value) {
    return;
  }

  await deleteChargingStation(selectedRow.value.id);

  removeModalVisible.value = false;
  selectedRow.value = undefined;
}

async function importFile(file: File) {
  importResponse.value = await importChargingStations(file);
  importModalVisible.value = false;

  if (importResponse.value.failed.length) {
    importResultModalVisible.value = true;
  }
}

function retryImport() {
  importModalVisible.value = true;
  importResultModalVisible.value = false;

  // reset the import response
  importResponse.value = undefined;
}
</script>
